import React from "react"
import { useQuery } from "react-query"
import { AxiosError } from "axios"
import moment from "moment"
import { useNavigate } from "react-router-dom"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import { getAlertesStaffAvecDocsEmbaucheNonEnvoyesRequest, IStaffDocsEmbaucheNonEnvoyesItem } from "./_requests"
import StaffDocsEmbaucheSendModal from "./StaffDocsEmbaucheSendModal"
import { ROUTING_ABSOLUTE_PATH_GESTION_STRUCTURE_STAFF_DEMANDE_EMBAUCHE_DETAILS } from "../../../gestion/routing/routing-endpoints"

const REACT_QUERY_KEY_STAFF_DOCS_EMBAUCHE = "REACT_QUERY_KEY_STAFF_DOCS_EMBAUCHE"

const initials = (name: string) =>
     (name || "?")
          .split(/\s+/)
          .filter(Boolean)
          .slice(0, 2)
          .map(s => s.charAt(0))
          .join("")
          .toUpperCase() || "?"

// Section « Staff » de l'alerte documents d'embauche non envoyés — cartes responsives + envoi en modale.
const StaffDocsEmbaucheNonEnvoyesSection = ({ onChanged }: { onChanged?: () => void }) => {
     const navigate = useNavigate()
     const [sendItem, setSendItem] = React.useState<IStaffDocsEmbaucheNonEnvoyesItem | null>(null)

     const query = useQuery<IStaffDocsEmbaucheNonEnvoyesItem[], AxiosError>(REACT_QUERY_KEY_STAFF_DOCS_EMBAUCHE, async () =>
          getAlertesStaffAvecDocsEmbaucheNonEnvoyesRequest().then(r => r.data.data)
     )

     if (query.isFetching) {
          return (
               <div className="d-flex justify-content-center py-10">
                    <MySimpleSpinner size={"md"} />
               </div>
          )
     }
     if (query.isError) return <div className="alert alert-danger">Impossible de charger les alertes staff.</div>

     const items = query.data ?? []

     if (items.length === 0) {
          return (
               <div className="text-center text-muted py-15">
                    <i className="fas fa-circle-check fs-3x text-success mb-4 d-block" />
                    <div className="fs-5 fw-semibold text-gray-700">Tout est à jour</div>
                    <div className="fs-7">Les documents d'embauche de tous les salariés ont été envoyés.</div>
               </div>
          )
     }

     return (
          <>
               <div className="d-flex align-items-center mb-5">
                    <span className="badge badge-light-warning fs-7 fw-bold me-2">{items.length}</span>
                    <span className="text-muted fs-7">salarié(s) dont les documents d'embauche ne sont pas encore envoyés</span>
               </div>

               <div className="row g-4">
                    {items.map(item => {
                         const detailsPath = ROUTING_ABSOLUTE_PATH_GESTION_STRUCTURE_STAFF_DEMANDE_EMBAUCHE_DETAILS(item.demandeId)
                         return (
                              <div className="col-12 col-md-6 col-xxl-4" key={item.demandeId}>
                                   <div className="card card-flush h-100 border border-gray-300 shadow-sm">
                                        <div className="card-body d-flex flex-column p-5">
                                             {/* En-tête : identité */}
                                             <div className="d-flex align-items-center gap-3 mb-4">
                                                  <div className="symbol symbol-45px symbol-circle">
                                                       <span className="symbol-label bg-light-primary text-primary fw-bold">{initials(item.collaborateur)}</span>
                                                  </div>
                                                  <div className="min-w-0 flex-grow-1">
                                                       <div className="fw-bold fs-5 text-gray-900 text-truncate">{item.collaborateur || "—"}</div>
                                                       <div className="d-flex flex-wrap gap-1 mt-1">
                                                            {item.entite && <span className="badge badge-light-primary">{item.entite}</span>}
                                                            {item.statut && <span className="badge badge-light-info">{item.statut}</span>}
                                                       </div>
                                                  </div>
                                             </div>

                                             {/* Méta */}
                                             <div className="d-flex flex-column gap-2 fs-7 text-gray-700 mb-4">
                                                  <div className="d-flex align-items-center">
                                                       <i className="fas fa-flag text-muted w-20px" />
                                                       <span className="text-muted me-1">État :</span>
                                                       <span className="fw-semibold">{item.etatLabel ?? "—"}</span>
                                                  </div>
                                                  <div className="d-flex align-items-center">
                                                       <i className="fas fa-user-pen text-muted w-20px" />
                                                       <span className="text-muted me-1">Créateur :</span>
                                                       <span className="fw-semibold text-truncate">{item.createur ?? "—"}</span>
                                                  </div>
                                                  <div className="d-flex align-items-center">
                                                       <i className="fas fa-calendar-day text-muted w-20px" />
                                                       <span className="text-muted me-1">Demande :</span>
                                                       <span className="fw-semibold">{item.dateDemande ? moment(item.dateDemande).format("DD/MM/YYYY") : "—"}</span>
                                                  </div>
                                             </div>

                                             {/* Actions */}
                                             <div className="d-flex gap-2 mt-auto pt-3 border-top border-gray-200">
                                                  <button className="btn btn-sm btn-light flex-grow-1" onClick={() => navigate(detailsPath)}>
                                                       <i className="fas fa-eye me-1" /> Détails
                                                  </button>
                                                  <button className="btn btn-sm btn-success flex-grow-1" onClick={() => setSendItem(item)}>
                                                       <i className="fas fa-paper-plane me-1" /> Envoyer
                                                  </button>
                                             </div>
                                        </div>
                                   </div>
                              </div>
                         )
                    })}
               </div>

               {sendItem && (
                    <StaffDocsEmbaucheSendModal
                         demandeId={sendItem.demandeId}
                         collaborateur={sendItem.collaborateur}
                         onClose={() => setSendItem(null)}
                         onSent={() => {
                              setSendItem(null)
                              query.refetch()
                              onChanged?.()
                         }}
                    />
               )}
          </>
     )
}

export default StaffDocsEmbaucheNonEnvoyesSection
